import { either } from 'fp-ts';
import * as t from 'io-ts';
import rangy from 'rangy';
import { Subject, Subscription } from 'rxjs';
import { scan } from 'rxjs/operators';
import * as NodeUtil from './NodeUtils';
import { setTimeoutOnWorker } from './scheduler';

type EventType = PointerEvent;
const coordinatesFromEvent = (event: EventType) => ({
  clientX: event.clientX,
  clientY: event.clientY,
  pageX: event.pageX,
  pageY: event.pageY,
});
const defaultMapBounds = ({
  getBoundingClientRect,
}: {
  getBoundingClientRect: () => DOMRect;
}) => getBoundingClientRect();

type BoundsMapper = (el: HTMLElement) => DOMRect;

const userAgentMatch = function (regExp: RegExp) {
  return regExp.exec(window.navigator.userAgent);
};

const testUserAgent = function (regExp: RegExp) {
  return regExp.test(window.navigator.userAgent);
};
export type Coordinate = {
  clientX: number;
  clientY: number;
  pageX: number;
  pageY: number;
};

export type Position<N extends ChildNode = ChildNode> = {
  node: N;
  rect: DOMRect;
};
export type Environment = {
  isWebkit: boolean;
  isAppleDevice: boolean;
  isAndroidLowerThanKitkat: boolean;
  isAndroidStackBrowser: boolean;
};

export class SelectionApplier {
  constructor(
    public settings: Options,
    public lastSelection: LastSelection,
    public markersWrapper: MarkersWrapper,
    public selectionDrawer: SelectionDrawer,
    public selectionStateStore: SelectionStateStore
  ) {}
  applySelectionFor = (range: RangyRange) => {
    if (range != null) {
      if (!this.lastSelection.rangeEqualsTo(range)) {
        const drawResult = this.drawSelection(range);

        this.selectionStateStore.setState((state) => ({
          range,
          endHeight: drawResult.endHeight,
          startHeight: drawResult.startHeight,
          selecting: state ? state.selecting : false,
        }));

        this.lastSelection.range = range;
      }
      return this.markersWrapper.showMarkers();
    }
  };

  refreshSelection = () => {
    if (this.lastSelection.exists(this.lastSelection.range)) {
      return this.drawSelection(this.lastSelection.range);
    } else {
      return null;
    }
  };

  removeCanvas = () => {
    this.selectionDrawer.removeCanvas();
  };
  clearSelection = () => {
    if (this.lastSelection.exists()) {
      this.selectionDrawer.clearSelection();
      this.selectionStateStore.setState(() => undefined);
    }
    this.markersWrapper.hideMarkers();
    this.lastSelection.range = undefined;
  };
  drawSelection = (range: RangyRange) => {
    this.markersWrapper.alignMarkersToRange(range);
    return this.selectionDrawer.redraw(range);
  };
}

export class SelectionStateStore {
  private stateMutator = new Subject<
    (state?: SelectionState) => SelectionState | undefined
  >();
  public state: SelectionState | undefined;
  private subscription: Subscription;
  constructor(onSelectionChange: (state?: SelectionState) => void) {
    this.subscription = this.stateMutator
      .pipe(
        scan(
          (
            acc,
            mapper: (state?: SelectionState) => SelectionState | undefined
          ) => {
            const next = mapper(acc.state);
            if (next === acc.state) {
              return {
                action: 'none' as const,
                selectingChanged: false,
                state: next,
              };
            } else {
              const selectingChanged =
                (next ? next.selecting : undefined) !==
                (acc.state ? acc.state.selecting : undefined);

              return {
                action: 'change' as const,
                selectingChanged,
                state: next,
              };
            }
          },
          {
            action: 'none' as 'none' | 'change',
            selectingChanged: false,
            state: undefined as SelectionState | undefined,
          }
        )
      )
      .subscribe((e) => {
        if (e.action === 'change') {
          this.state = e.state;
          onSelectionChange(e.state);
        }
      });
  }
  setState = (
    mapper: (state?: SelectionState) => SelectionState | undefined
  ) => {
    this.stateMutator.next(mapper);
  };
  destroy = () => {
    this.subscription.unsubscribe();
  };
}

export class PointerEventBinder {
  constructor(
    public element: HTMLElement,
    public markersWrapper: MarkersWrapper,
    public pointerEventBus: PointerEventBus
  ) {
    const ownerDocument = element.ownerDocument;
    if (!ownerDocument) {
      throw new Error('Element has no document');
    }

    this.markersWrapper.startMarker.markerElement.addEventListener(
      'pointerdown',
      this.pointerEventBus.handleMarkerPointerDown,
      { passive: false }
    );
    this.markersWrapper.endMarker.markerElement.addEventListener(
      'pointerdown',
      this.pointerEventBus.handleMarkerPointerDown,
      { passive: false }
    );

    this.markersWrapper.endMarker.markerElement.addEventListener(
      'pointermove',
      this.pointerEventBus.handleMarkerPointerMove,
      { passive: false }
    );
    this.markersWrapper.startMarker.markerElement.addEventListener(
      'pointermove',
      this.pointerEventBus.handleMarkerPointerMove,
      { passive: false }
    );
    this.markersWrapper.endMarker.markerElement.addEventListener(
      'pointerup',
      this.pointerEventBus.handleMarkerPointerUp,
      { passive: false }
    );
    this.markersWrapper.startMarker.markerElement.addEventListener(
      'pointerup',
      this.pointerEventBus.handleMarkerPointerUp,
      { passive: false }
    );

    this.element.addEventListener(
      'pointerdown',
      this.pointerEventBus.handleTapHoldStart
    );
    this.element.addEventListener(
      'pointerup',
      this.pointerEventBus.handleTapHoldCancel
    );
    this.element.addEventListener(
      'pointercancel',
      this.pointerEventBus.handleTapHoldCancel
    );

    /*    this.element.addEventListener(
      "touchmove",
      this.pointerEventBus.handleTapHoldCancel
    ); */
  }

  destroyBindings = () => {
    this.markersWrapper.startMarker.markerElement.removeEventListener(
      'pointerdown',
      this.pointerEventBus.handleMarkerPointerDown
    );
    this.markersWrapper.endMarker.markerElement.removeEventListener(
      'pointerdown',
      this.pointerEventBus.handleMarkerPointerDown
    );

    this.markersWrapper.endMarker.markerElement.removeEventListener(
      'pointermove',
      this.pointerEventBus.handleMarkerPointerMove
    );
    this.markersWrapper.startMarker.markerElement.removeEventListener(
      'pointermove',
      this.pointerEventBus.handleMarkerPointerMove
    );
    this.markersWrapper.endMarker.markerElement.removeEventListener(
      'pointerup',
      this.pointerEventBus.handleMarkerPointerUp
    );
    this.markersWrapper.startMarker.markerElement.removeEventListener(
      'pointerup',
      this.pointerEventBus.handleMarkerPointerUp
    );

    this.element.removeEventListener(
      'pointerdown',
      this.pointerEventBus.handleTapHoldStart
    );
    this.element.removeEventListener(
      'pointerup',
      this.pointerEventBus.handleTapHoldCancel
    );
    this.element.removeEventListener(
      'pointercancel',
      this.pointerEventBus.handleTapHoldCancel
    );

    /*     this.element.removeEventListener(
      "touchmove",
      this.pointerEventBus.handleTapHoldCancel
    ); */
  };
}

export class PointerEventBus {
  rejectTouchEnd = false;
  holdTask?: ReturnType<typeof setTimeoutOnWorker>;
  constructor(
    private movingMarker: MovingMarker,
    private selectionApplier: SelectionApplier,
    private selectionConstructor: SelectionConstructor,
    private selectionStateStore: SelectionStateStore,
    private markersWrapper: MarkersWrapper,
    private contentContext: ContentContext,
    private contextTranslator: ContextTranslator,
    private options: {
      holdTime: number;
    }
  ) {}

  handleTapHoldStart = (event: EventType) => {
    if (event.target && this.markersWrapper.isMarkerElement(event.target)) {
      return;
    }

    this.holdTask = setTimeoutOnWorker(this.options.holdTime);
    this.holdTask.promise
      .then(() => {
        this.holdTask = undefined;

        if (!this.selectionStateStore.state) {
          this.selectWordUnderPointer(event);
        }
      })
      .catch(() => {
        this.selectionApplier.clearSelection();
      });
  };
  handleTapHoldCancel = (event: EventType) => {
    if (this.holdTask) {
      this.holdTask.cancel();
    }
  };

  handleMarkerPointerDown = (event: EventType) => {
    if (event.target && this.contentContext.isHTMLElement(event.target)) {
      this.movingMarker.setTo(event.target);
    }
  };

  handleMarkerPointerMove = (event: EventType) => {
    if (this.movingMarker.exists()) {
      event.preventDefault();
      this.selectionStateStore.setState((state) =>
        state && !state.selecting ? { ...state, selecting: true } : state
      );

      this.updateSelectionWithPointerMove(event);
    }
  };
  handleMarkerPointerUp = (event: EventType) => {
    event.preventDefault();
    this.movingMarker.unset();

    this.selectionStateStore.setState((state) =>
      state ? { ...state, selecting: false } : state
    );
  };
  handleGlobalTap = () => {
    this.selectionApplier.clearSelection();
  };
  selectWordUnderPointer = (event: EventType) => {
    this.contextTranslator.updatePointerTransform();
    let range;

    if ((range = this.selectionConstructor.getWordSelectionFrom(event))) {
      this.movingMarker.unset();
      this.selectionApplier.clearSelection();
      this.selectionApplier.applySelectionFor(range);
    }
    return;
  };
  updateSelectionWithPointerMove = (event: EventType) => {
    return this.selectionConstructor.getSelectionUpdatedWith(
      event,
      (range: RangyRange) => {
        return this.selectionApplier.applySelectionFor(range);
      }
    );
  };
  cancelPointerUpDefaults = (event: EventType) => {
    event.preventDefault();
    event.stopPropagation();
  };
}
export class SelectionRangeBuilder {
  constructor(
    public pointToRangeConverter: PointToRangeConverter,
    public selectionBoundFactory: SelectionBoundFactory,
    public movingMarker: MovingMarker
  ) {}
  getRangeUpdatedWithPoint = (point: Point) => {
    let coveringRange = null;
    const anchor = this.pointToRangeConverter.pointToRangeAnchor(point);

    if (anchor) {
      coveringRange = ContentContext.createRange();
      const movingBound =
        this.selectionBoundFactory.getMovingSelectionBound(anchor);
      const protectedBound =
        this.selectionBoundFactory.getProtectedSelectionBound();
      if (protectedBound) {
        movingBound.applyTo(coveringRange);
        protectedBound.applyTo(coveringRange);
        if (coveringRange.collapsed) {
          protectedBound.applyOppositeTo(coveringRange);
          movingBound.applyOppositeTo(coveringRange);
          this.movingMarker.toggleMoving();
        }
      }
    }
    return coveringRange;
  };
}

export class SelectionConstructor {
  lastPoint?: Point;
  constructor(
    private settings: Options,
    private selectionRangeBuilder: SelectionRangeBuilder,
    private markersWrapper: MarkersWrapper,
    private pointFactory: PointFactory,
    private wordRangeBuilder: WordRangeBuilder,
    private frameRequester: FrameRequester,
    private contentContext: ContentContext
  ) {}

  getWordSelectionFrom = (event: EventType) => {
    try {
      const eventTarget = event.target;
      if (!eventTarget || !this.contentContext.isHTMLElement(eventTarget)) {
        return null;
      }
      if (!this.markersWrapper.isMarkerElement(eventTarget)) {
        const point = this.pointFactory.createFromContentEvent(event);
        if (!point) {
          return null;
        }
        const selectedRange =
          this.wordRangeBuilder.getRangeOfWordUnderPoint(point);

        return selectedRange;
      }
    } catch (e) {}

    return null;
  };
  getSelectionUpdatedWith = (
    event: EventType,
    onRangeReady: (range: RangyRange) => void
  ) => {
    this.lastPoint = this.pointFactory.createFromMarkerEvent(
      event,
      -this.settings.markerShiftY
    );

    return this.frameRequester.requestFrame(() => {
      if (this.lastPoint) {
        const range = this.selectionRangeBuilder.getRangeUpdatedWithPoint(
          this.lastPoint
        );
        if (range) return onRangeReady(range);
      }
    });
  };
}

export class PointFactory {
  constructor(
    public environment: Environment,
    public contextTranslator: ContextTranslator,
    public pointTargetLocator: PointTargetLocator
  ) {}

  createFromContentEvent = (event: EventType) => {
    //TODO: This is kind of messed up. This gets called from tap+hold, and a pointer position is determined, but not shifted, but when a marker is moved...we shift the pointer....
    const pointer = this.getPointerFromEvent(event);
    const target = this.pointTargetLocator.getTargetFromEvent(event);
    if (!target) {
      return null;
    }
    return new Point(pointer, target);
  };
  createFromMarkerEvent = (event: EventType, shiftY: number) => {
    const pointer = this.shiftPointer(this.getPointerFromEvent(event));

    const target = this.pointTargetLocator.getTargetByCoords(pointer);

    return new Point(pointer, target);
  };

  createFromClientCoordsInText = (coords: Coordinate, textNode: Text) => {
    const target = {
      node: textNode,
      isInText: true,
    };
    return new Point(coords, target);
  };
  getPointerFromEvent = (event: EventType) => {
    return this.clonePointer(coordinatesFromEvent(event));
  };

  clonePointer = (pointer: Coordinate): Coordinate => {
    return {
      pageX: pointer.pageX,
      pageY: pointer.pageY,
      clientX: pointer.clientX,
      clientY: pointer.clientY,
    };
  };

  shiftPointer = (pointer: Coordinate) => {
    return {
      pageY: pointer.pageY + this.contextTranslator.pointerTransform.offsetY,
      pageX: pointer.pageX + this.contextTranslator.pointerTransform.offsetX,
      clientY:
        pointer.clientY + this.contextTranslator.pointerTransform.offsetY,
      clientX:
        pointer.clientX + this.contextTranslator.pointerTransform.offsetX,
    };
  };
}

export type Anchor = { container: Node; offset: number };

export abstract class RangeBoundary {
  constructor(public anchor: Anchor) {}
  get container() {
    return this.anchor.container;
  }
  get offset() {
    return this.anchor.offset;
  }
  abstract applyTo: (range: RangyRange) => void;
  abstract applyOppositeTo: (range: RangyRange) => void;
}

export class EndBoundary extends RangeBoundary {
  constructor(anchor: Anchor) {
    super(anchor);
  }
  applyTo = (range: RangyRange) => {
    range.setEnd(this.container, this.offset);
  };
  applyOppositeTo = (range: RangyRange) => {
    range.setStart(this.container, this.offset);
  };
}

export class StartBoundary extends RangeBoundary {
  constructor(anchor: Anchor) {
    super(anchor);
  }
  applyTo = (range: RangyRange) => {
    return range.setStart(this.container, this.offset);
  };
  applyOppositeTo = (range: RangyRange) => {
    return range.setEnd(this.container, this.offset);
  };
}

export class SelectionBoundFactory {
  constructor(
    public lastSelection: LastSelection,
    public movingMarker: MovingMarker
  ) {}
  getProtectedSelectionBound = () => {
    if (!this.lastSelection.range) {
      return;
    }
    if (this.movingMarker.isStartMarker()) {
      return this.createEndBoundary({
        container: this.lastSelection.range.endContainer,
        offset: this.lastSelection.range.endOffset,
      });
    } else {
      return this.createStartBoundary({
        container: this.lastSelection.range.startContainer,
        offset: this.lastSelection.range.startOffset,
      });
    }
  };
  getMovingSelectionBound = (anchor: Anchor) => {
    if (this.movingMarker.isEndMarker()) {
      return this.createEndBoundary(anchor);
    } else {
      return this.createStartBoundary(anchor);
    }
  };

  createEndBoundary = (anchor: Anchor) => {
    return new EndBoundary(anchor);
  };

  createStartBoundary = (anchor: any) => {
    return new StartBoundary(anchor);
  };
}
export abstract class PointSnapper {
  constructor(
    public pointFactory: PointFactory,
    private contentContext: ContentContext
  ) {}
  snapPointToTextInElement = (point: Point, element: ChildNode) => {
    const position = this.searchPositionInTextByPoint(element, point);

    if (position) {
      return this.createPointAt(position);
    }
    return null;
  };
  searchPositionInTextByPoint = (el: ChildNode, point: Point) => {
    let node = el;
    let position = null;
    while ((position = this.searchPositionWithin(node, point))) {
      if (NodeUtil.nodeIsText(position.node)) {
        return position;
      } else {
        node = position.node;
      }
    }
    return null;
  };
  searchPositionWithin = (element: Node, point: Point) => {
    let position: Position | null = null;
    let _ref = (element != null ? element.childNodes : void 0) || [];
    for (let _i = 0, _len = _ref.length; _i < _len; _i++) {
      let node = _ref[_i];
      if (this.nodeMightBeClosest(node)) {
        const nextPos = this.getPositionCloserToPoint(position, node, point);
        if (nextPos) {
          position = nextPos;
        }
      }
    }

    return position;
  };

  getPositionCloserToPoint = (
    winningPosition: Position | null,
    rivalingNode: ChildNode,
    point: Point
  ) => {
    const rivalingRect = this.getClosestRectFromNode(rivalingNode, point);
    if (!rivalingRect) {
      return null;
    }
    if (this.rivalingRectWins(winningPosition, rivalingRect)) {
      return {
        node: rivalingNode,
        rect: rivalingRect,
      };
    } else {
      return null;
    }
  };
  rivalingRectWins = (winningPosition: Position | null, rivalingRect: any) => {
    return (
      (winningPosition != null &&
        this.isRivalingRectCloser(rivalingRect, winningPosition.rect)) ||
      (winningPosition == null && rivalingRect != null)
    );
  };

  isRivalingRectCloser = (
    rivalingRect: DOMRect | ClientRect,
    winningRect: DOMRect | ClientRect
  ) => {
    return (
      rivalingRect != null &&
      winningRect != null &&
      rivalingRect !== winningRect &&
      this.getCloserRect(rivalingRect, winningRect) === rivalingRect
    );
  };

  nodeMightBeClosest = (node: Node) => {
    return (
      NodeUtil.nodeHasRects(node) &&
      (NodeUtil.nodeHasChildren(node) || NodeUtil.nodeIsText(node)) &&
      this.contentContext.isElementContained(node)
    );
  };

  createPointAt = (position: Position) => {
    return NodeUtil.nodeIsText(position.node)
      ? this.pointFactory.createFromClientCoordsInText(
          {
            clientX: position.rect.right - 1,
            clientY: position.rect.bottom - 1,
            pageX: position.rect.right - 1,
            pageY: position.rect.bottom - 1,
          },
          position.node
        )
      : null;
  };

  abstract getCloserRect: (
    rectA: DOMRect | ClientRect,
    rectB: DOMRect | ClientRect
  ) => DOMRect | ClientRect | null;

  abstract getClosestRectFromNode: (
    node: Node,
    point: Coordinate
  ) => DOMRect | ClientRect | null;
}
export class RightPointSnapper extends PointSnapper {
  constructor(
    public override pointFactory: PointFactory,
    contentContext: ContentContext
  ) {
    super(pointFactory, contentContext);
  }
  getClosestRectFromNode = (node: Node, point: Coordinate) => {
    const rectsResult = NodeUtil.getRectsForNode(node);
    if (either.isLeft(rectsResult)) {
      return null;
    }
    const rects = rectsResult.right;

    let nearestRect: ClientRect | DOMRect | null = null;
    for (let _i = 0, _len = rects.length; _i < _len; _i++) {
      const rect = rects[_i];

      if (
        this.rectIsInTheSameLineOnLeft(rect, point) &&
        (!nearestRect || rect.right > nearestRect.right)
      ) {
        nearestRect = rect;
      }
    }

    return nearestRect;
  };
  getCloserRect = (
    rectA: DOMRect | ClientRect,
    rectB: DOMRect | ClientRect
  ) => {
    if (rectA.right > rectB.right) {
      return rectA;
    } else if (rectB.right > rectA.right) {
      return rectB;
    } else {
      return null;
    }
  };

  rectIsInTheSameLineOnLeft = (
    rect: DOMRect | ClientRect,
    point: Coordinate
  ) => {
    const x = point.clientX;
    const y = point.clientY;
    return rect.right < x && rect.top <= y && rect.bottom >= y;
  };
}

export class BelowPointSnapper extends PointSnapper {
  constructor(
    public override pointFactory: PointFactory,
    contentContext: ContentContext
  ) {
    super(pointFactory, contentContext);
  }

  getClosestRectFromNode = (node: Node, point: Coordinate) => {
    const y = point.clientY;
    const rectsResult = NodeUtil.getRectsForNode(node);
    if (either.isLeft(rectsResult)) {
      return null;
    }
    const rects = rectsResult.right;

    let nearestRect: ClientRect | DOMRect | null = null;
    for (let _i = 0, _len = rects.length; _i < _len; _i++) {
      const rect = rects[_i];
      if (rect.top < y && (!nearestRect || rect.top >= nearestRect.top)) {
        nearestRect = rect;
      }
    }
    return nearestRect;
  };
  getCloserRect = (
    rectA: DOMRect | ClientRect,
    rectB: DOMRect | ClientRect
  ) => {
    if (rectA.top >= rectB.top) {
      return rectA;
    } else if (rectB.top > rectA.top) {
      return rectB;
    } else {
      return null;
    }
  };
}

export class PointTargetLocator {
  constructor(
    public contentContext: ContentContext,
    public markersWrapper: MarkersWrapper,
    public pointLocator: PointLocator
  ) {}
  getTargetFromEvent = (event: EventType) => {
    const target = event.target;
    if (!target || !this.contentContext.isHTMLElement(target)) {
      return null;
    }
    const coords = coordinatesFromEvent(event);
    const textNode = this.locateTextNodeWithinElementByCoords(target, coords);
    return {
      node: textNode || target,
      isInText: textNode != null,
    };
  };

  getTargetByCoords = (coords: Coordinate) => {
    const targetElement = this.getTargetElementByCoords(coords);
    const textNode = this.locateTextNodeWithinElementByCoords(
      targetElement,
      coords
    );
    return {
      node: textNode || targetElement,
      isInText: textNode != null,
    };
  };

  // getTargetElementFromPointerEvent = (
  //   pointerEvent: HammerInput
  // ): HTMLElement => {
  //   return pointerEvent.target;
  // };
  getTargetElementByCoords = (coords: Coordinate) => {
    this.markersWrapper.hideMarkers();
    const element =
      this.contentContext.getElementByPoint(coords) || this.contentContext.body;
    this.markersWrapper.showMarkers();
    return element;
  };

  locateTextNodeWithinElementByCoords = (
    element: ChildNode,
    pointer: Coordinate
  ) => {
    const children = element.childNodes;
    for (let _i = 0, _len = children.length; _i < _len; _i++) {
      let child = children[_i];
      if (this.isPointerInText(child, pointer)) {
        return child;
      }
    }
    return null;
  };

  isPointerInText = (node: Node, point: Coordinate) => {
    return (
      NodeUtil.nodeIsText(node) &&
      this.pointLocator.nodeContainsPoint(node, point)
    );
  };
}

export class EnvironmentChecker {
  isAppleDevice = () => {
    return testUserAgent(/(iPad|iPhone|iPod)/g);
  };

  isAndroidStackBrowser = () => {
    return this.isAndroid() && testUserAgent(/Version\/\d\.\d/g);
  };

  isAndroidLowerThan = (versionString: string) => {
    let actualMajor,
      actualMinor,
      actualVersion,
      expectedMajor,
      expectedMinor,
      expectedVersion;
    try {
      if (!this.isAndroid()) {
        return false;
      }
      expectedVersion = versionString.split('.');
      expectedMajor = Number(expectedVersion[0]);
      expectedMinor = Number(expectedVersion[1]);
      actualVersion = userAgentMatch(/Android (\d)\.(\d)/g);
      if (!actualVersion) {
        throw new Error('Could not exec regex');
      }
      actualMajor = Number(actualVersion[1]);
      actualMinor = Number(actualVersion[2]);
      return (
        actualMajor < expectedMajor ||
        (actualMajor === expectedMajor && actualMinor < expectedMinor)
      );
    } catch (e) {
      return false;
    }
  };

  isAndroid = () => {
    return testUserAgent(/Android/g);
  };

  isWebkit = () => {
    return testUserAgent(/WebKit/g);
  };
}

export class FrameRequester {
  ticking = false;
  requestFrame = (func: () => void) => {
    if (!this.ticking) {
      window.requestAnimationFrame(
        (function (_this) {
          return function () {
            func();
            return (_this.ticking = false);
          };
        })(this)
      );
    }
    return (this.ticking = true);
  };
}

export class PointToRangeConverter {
  constructor(
    public pointLocator: PointLocator,
    public contentContext: ContentContext,
    public rightPointSnapper: RightPointSnapper,
    public belowPointSnapper: BelowPointSnapper
  ) {}

  pointToRange = (point: Point) => {
    const range = ContentContext.createRange();
    let startIndex = 0;
    const maxIndex = Point.isTextPoint(point) ? point.target.data.length : 0;
    let endIndex = maxIndex;
    while (startIndex < endIndex) {
      let middle = (startIndex + endIndex) >> 1;
      range.setStart(point.target, startIndex);
      range.setEnd(point.target, middle + 1);
      if (this.pointLocator.rangeContainsPoint(range, point)) {
        endIndex = middle;
      } else {
        startIndex = middle + 1;
        range.setStart(point.target, startIndex);
        range.setEnd(point.target, endIndex);
      }
    }
    if (range.collapsed && range.endOffset < maxIndex) {
      range.setEnd(point.target, range.endOffset + 1);
    }

    return range;
  };
  pointToRangeAnchor = (point: Point) => {
    const range = this.pointToRange(point);

    if (Point.isTextPoint(point) && range) {
      const startAnchor = this.getStartAnchorOf(range);
      return startAnchor;
    } else {
      const newPoint = this.snapPointToText(point);
      if (!newPoint) {
        return null;
      }

      const pointOfRange = this.pointToRange(newPoint);

      if (pointOfRange) {
        const endAnchor = this.getEndAnchorOf(pointOfRange);
        return endAnchor;
      } else {
        return null;
      }
    }
  };
  snapPointToText = (point: Point) => {
    return (
      this.rightPointSnapper.snapPointToTextInElement(point, point.target) ||
      this.belowPointSnapper.snapPointToTextInElement(point, point.target)
    );
  };
  getStartAnchorOf = (range: RangyRange) => {
    return {
      container: range.startContainer,
      offset: range.startOffset,
    };
  };

  getEndAnchorOf = function (range: RangyRange) {
    return {
      container: range.endContainer,
      offset: range.endOffset,
    };
  };
}

const WHITESPACE_LIST = [' ', '\t', '\r', '\n'];
export class WordRangeBuilder {
  constructor(
    public pointToRangeConverter: PointToRangeConverter,
    public containerBounds: HTMLElement
  ) {}

  getRangeOfWordUnderPoint = (point: Point) => {
    var range;
    range = null;

    if (Point.isTextPoint(point)) {
      range = this.pointToRangeConverter.pointToRange(point);
      this.expandRangeToStartAfterTheWhitespaceOnLeft(range);
      this.expandRangeToEndBeforeTheWhitespaceOnRight(range);
      const wholeElementRange = rangy.createRange();
      wholeElementRange.selectNodeContents(this.containerBounds);
      const intersection = wholeElementRange.intersection(range);
      range = intersection;
    }
    return range;
  };

  expandRangeToStartAfterTheWhitespaceOnLeft = (range: RangyRange) => {
    while (!this.rangeStartsWithWhitespace(range)) {
      if (range.startOffset > 0) {
        range.setStart(range.startContainer, range.startOffset - 1);
      } else {
        /* if (!this._putRangeStartAtTheEndOfPreviousTextNode(range)) {
            return;
          } */
        break;
      }
    }
    if (/^\s/.test(range.toString())) {
      range.setStart(range.startContainer, range.startOffset + 1);
    }

    /*  if (this._rangeStartsOnWhitespaceAtTheEndOfNode(range)) {
        return range.setStart(
          this._nodeUtil.getTextNodeAfter(range.startContainer),
          0
        );
      } */
  };
  expandRangeToEndBeforeTheWhitespaceOnRight = (range: RangyRange) => {
    let maxIndex;
    while (!this.rangeEndsWithWhitespace(range)) {
      if (!('data' in range.endContainer)) return;
      maxIndex = (range.endContainer as any).data.length;
      if (range.endOffset < maxIndex) {
        range.setEnd(range.endContainer, range.endOffset + 1);
      } else {
        /* if (!this._putRangeEndAtTheBeginningOfNextTextNode(range)) {
            return;
          } */
        break;
      }
    }
    if (/\s$/.test(range.toString())) {
      range.setEnd(range.endContainer, Math.max(range.endOffset - 1, 0));
    }
  };

  rangeStartsWithWhitespace = (range: RangyRange) => {
    const firstLetter = range.toString()[0];
    return WHITESPACE_LIST.indexOf(firstLetter) >= 0;
  };

  rangeEndsWithWhitespace = (range: RangyRange) => {
    const stringified = range.toString();
    const lastLetter = stringified[stringified.length - 1];
    return WHITESPACE_LIST.indexOf(lastLetter) >= 0;
  };

  putRangeStartAtTheEndOfPreviousTextNode = (range: RangyRange) => {
    let newStartContainer;
    if (
      (newStartContainer = NodeUtil.getTextNodeBefore(range.startContainer))
    ) {
      range.setStart(newStartContainer, newStartContainer.data.length);
      return true;
    } else {
      return false;
    }
  };

  putRangeEndAtTheBeginningOfNextTextNode = (range: RangyRange) => {
    let newEndContainer;
    if ((newEndContainer = NodeUtil.getTextNodeAfter(range.endContainer))) {
      range.setEnd(newEndContainer, 0);
      return true;
    } else {
      return false;
    }
  };

  rangeStartsOnWhitespaceAtTheEndOfNode = (range: RangyRange) => {
    return (
      NodeUtil.nodeIsText(range.startContainer) &&
      this.nodeEndsWithWhitespace(range.startContainer) &&
      (range.startOffset === (range.startContainer as any).data.length ||
        range.startOffset === (range.startContainer as any).data.length - 1)
    );
  };

  nodeEndsWithWhitespace = (node: Text) => {
    var lastLetter;
    lastLetter = node.data[node.data.length - 1];
    return WHITESPACE_LIST.indexOf(lastLetter) >= 0;
  };
}

export class ContentContext {
  public document: Document;
  public body: HTMLElement;
  public containerRange: RangyRange;
  constructor(public container: HTMLElement) {
    if (!container.ownerDocument) {
      throw new Error('Container does not have an owner document');
    }

    this.document = container.ownerDocument;

    this.body = this.document.body;
    this.containerRange = ContentContext.createRange();
    this.containerRange.selectNode(container);
  }

  containRange = (range: RangyRange) => this.containerRange.intersection(range);

  isElementContained = (el: Node) => this.container.contains(el);

  isHTMLElement = (el: unknown): el is HTMLElement => {
    return ContentContext.HTMLElementStructure.is(el)
      ? el.__proto__.__proto__.toString().startsWith('[object HTMLElement')
      : false;
  };
  static HTMLElementStructure = t.type({
    __proto__: t.type({
      __proto__: t.type({
        toString: t.Function,
      }),
    }),
  });
  static createRange = () => {
    return rangy.createRange();
  };
  createElement = <T extends keyof HTMLElementTagNameMap>(
    tagName: T
  ): HTMLElementTagNameMap[T] => {
    return this.document.createElement(tagName);
  };

  getElementByPoint = (point: Coordinate) => {
    return this.document.elementFromPoint(point.clientX, point.clientY);
  };
}

export class LastSelection {
  range?: RangyRange;

  cloneRange = () => {
    return this.range ? this.range.cloneRange() : undefined;
  };

  exists = (range = this.range): range is RangyRange => {
    return (
      !!this.range &&
      this.range.nativeRange.getBoundingClientRect() != null &&
      this.range.nativeRange.getClientRects().length > 0
    );
  };

  rangeEqualsTo = (range: RangyRange) => {
    return this.range && this.hasSameStartAs(range) && this.hasSameEndAs(range);
  };

  hasSameStartAs = (range: RangyRange) => {
    return this.range
      ? this.range.compareBoundaryPoints(Range.START_TO_START, range) === 0
      : false;
  };

  hasSameEndAs = (range: RangyRange) => {
    return this.range
      ? this.range.compareBoundaryPoints(Range.END_TO_END, range) === 0
      : false;
  };
}

export class ContextTranslator {
  constructor(
    public mapBounds: BoundsMapper = defaultMapBounds,
    private pointerEventsBounds?: HTMLElement
  ) {}
  transform = { offsetX: 0, offsetY: 0, scale: 0 };
  pointerTransform = { offsetX: 0, offsetY: 0 };
  body?: HTMLElement;

  setBody = (body: HTMLElement) => {
    return (this.body = body);
  };

  updatePointerTransform = () => {
    if (this.pointerEventsBounds) {
      const rect = this.mapBounds(this.pointerEventsBounds);
      this.pointerTransform.offsetX = rect.left;
      this.pointerTransform.offsetY = rect.top;
    }
  };

  setContentTransformationFromMarkersContext = (transformation: {
    offsetX: number;
    offsetY: number;
    scale: number;
  }) => {
    this.transform.offsetX = transformation.offsetX;
    this.transform.offsetY = transformation.offsetY;
    return (this.transform.scale = transformation.scale);
  };

  contentXToMarkerContext = (contentX: number) => {
    return contentX * this.transform.scale + this.transform.offsetX;
  };

  contentYToMarkerContext = (contentY: number) => {
    return contentY * this.transform.scale + this.transform.offsetY;
  };

  scaleToContentContext = (n: number) => {
    return n / this.transform.scale;
  };

  markersYToContentContext = (markersY: number) => {
    return this.scaleToContentContext(markersY - this.transform.offsetY);
  };

  markersXToContentContext = (markersX: number) => {
    return this.scaleToContentContext(markersX - this.transform.offsetX);
  };

  adjustRect = (rect: DOMRect) => {
    const newRect = {
      left: rect.left + this.transform.offsetX,
      right: rect.right + this.transform.offsetX,
      top: rect.top + this.transform.offsetY,
      bottom: rect.bottom + this.transform.offsetY,
      width: rect.width,
      height: rect.height,
      x: rect.left + this.transform.offsetX,
      y: rect.top + this.transform.offsetY,
    };
    return {
      toJSON: () => newRect,
      ...newRect,
    };
  };
}

export abstract class Marker {
  constructor(
    public markerElement: HTMLElement,
    public contentContext: ContentContext,
    public contextTranslator: ContextTranslator,
    public mapBounds?: (el: HTMLElement) => DOMRect,
    public boundsElement?: HTMLElement
  ) {}

  hide = () => {
    this.markerElement.style.visibility = 'hidden';
    this.markerElement.style.display = 'none';
  };

  show = () => {
    this.markerElement.style.visibility = 'visible';
    this.markerElement.style.display = 'block';
  };

  disablePointerEvents = () => {
    this.markerElement.style.pointerEvents = 'none';
  };
  enablePointerEvents = () => {
    return (this.markerElement.style.pointerEvents = 'auto');
  };
  setMoving = (isMoving: boolean) => {
    //return this.element.toggleClass(MOVING_MARKER_CLASS, isMoving);
  };
  abstract alignToRange: (range: RangyRange) => void;
  createMarkerElement = () => {
    const element = this.contentContext.createElement('div');
    /*       element.setAttribute("class", MARKER_CLASS + " " + this._className); */
    element.setAttribute('style', 'position: absolute');
    this.contentContext.container.append(element);
    return element;
  };
}
export class StartMarker extends Marker {
  constructor(
    public override markerElement: HTMLElement,
    public override contentContext: ContentContext,
    public override contextTranslator: ContextTranslator,
    public override mapBounds?: (el: HTMLElement) => DOMRect,
    public override boundsElement?: HTMLElement
  ) {
    super(
      markerElement,
      contentContext,
      contextTranslator,
      mapBounds,
      boundsElement
    );
  }
  alignToRange = (range: RangyRange) => {
    const firstRect =
      range.nativeRange.getClientRects()[0] ||
      range.nativeRange.getBoundingClientRect();
    const mapBounds = this.mapBounds || defaultMapBounds;

    const containerBounds = !this.boundsElement
      ? {
          x: 0,
          y: 0,
          top: 0,
          left: 0,
          width: 0,
          height: 0,
        }
      : mapBounds(this.boundsElement);

    this.markerElement.style.left = `${
      firstRect.left - containerBounds.left
    }px`;
    this.markerElement.style.top = `${
      firstRect.bottom - containerBounds.top
    }px`;
  };
}

export class EndMarker extends Marker {
  constructor(
    public override markerElement: HTMLElement,
    public override contentContext: ContentContext,
    public override contextTranslator: ContextTranslator,
    public override mapBounds?: (el: HTMLElement) => DOMRect,
    public override boundsElement?: HTMLElement
  ) {
    super(
      markerElement,
      contentContext,
      contextTranslator,
      mapBounds,
      boundsElement
    );
  }
  alignToRange = (range: RangyRange) => {
    const rects = range.nativeRange.getClientRects();
    const lastRect = rects.length
      ? rects[rects.length - 1]
      : range.nativeRange.getBoundingClientRect();
    const mapBounds = this.mapBounds || defaultMapBounds;
    const containerBounds = !this.boundsElement
      ? {
          x: 0,
          y: 0,
          top: 0,
          left: 0,
          width: 0,
          height: 0,
        }
      : mapBounds(this.boundsElement);

    this.markerElement.style.left = `${
      lastRect.right - containerBounds.left
    }px`;
    this.markerElement.style.top = `${lastRect.bottom - containerBounds.top}px`;
  };
}

export class MovingMarker {
  public marker: Marker | null = null;
  constructor(public startMarker: StartMarker, public endMarker: EndMarker) {}
  setTo = (markerElement: HTMLElement) => {
    if (markerElement === this.startMarker.markerElement) {
      this.setMovingStart();
    } else {
      this.setMovingEnd();
    }
    /* this.startMarker.disablePointerEvents();
    this.endMarker.disablePointerEvents(); */
  };

  toggleMoving = () => {
    if (this.isStartMarker()) {
      return this.setMovingEnd();
    } else if (this.isEndMarker()) {
      return this.setMovingStart();
    }
  };
  unset = () => {
    this.marker = null;
    this.unsetMovingStart();
    this.unsetMovingEnd();
    this.startMarker.enablePointerEvents();
    return this.endMarker.enablePointerEvents();
  };
  isStartMarker = () => {
    return this.marker === this.startMarker;
  };
  isEndMarker = () => {
    return this.marker === this.endMarker;
  };
  exists = () => {
    return this.marker !== null;
  };
  setMovingStart = () => {
    this.unsetMovingEnd();
    this.marker = this.startMarker;
    return this.startMarker.setMoving(true);
  };
  setMovingEnd = () => {
    this.unsetMovingStart();
    this.marker = this.endMarker;
    return this.endMarker.setMoving(true);
  };
  unsetMovingStart = () => {
    return this.startMarker.setMoving(false);
  };
  unsetMovingEnd = () => {
    return this.endMarker.setMoving(false);
  };
}

export class MarkersWrapper {
  constructor(
    public startMarker: StartMarker,
    public endMarker: EndMarker,
    public contentContext: ContentContext
  ) {}
  showMarkers = () => {
    this.startMarker.show();
    this.endMarker.show();
  };
  hideMarkers = () => {
    this.startMarker.hide();
    this.endMarker.hide();
  };
  alignMarkersToRange = (range: RangyRange) => {
    this.startMarker.alignToRange(range);
    this.endMarker.alignToRange(range);
  };
  isMarkerElement = (element: EventTarget) => {
    return (
      element === this.startMarker.markerElement ||
      element === this.endMarker.markerElement
    );
  };
}

export class Rectangler {
  constructor(
    public environment: Environment,
    public contextTranslator: ContextTranslator
  ) {}
  getRectsFor = (range: RangyRange) => {
    let rects = ([] as DOMRect[]).slice.call(
      range.nativeRange.getClientRects()
    );

    if (this.environment.isWebkit) {
      rects = this.filterDuplicatedRects(rects);
    }
    return rects;
  };
  filterDuplicatedRects = (rects: DOMRect[]) => {
    const lastRect = rects[rects.length - 1];
    return rects.filter(this.isRectUnique.bind(this, rects, lastRect));
  };
  isRectUnique = (rects: DOMRect[], lastRect: DOMRect, rect: DOMRect) => {
    return (
      !this.rectEndsAfterLastRect(rect, lastRect) &&
      !this.rectContainsOneOfRects(rect, rects)
    );
  };
  rectEndsAfterLastRect = (rect: DOMRect, lastRect: DOMRect) => {
    const TOLERATED_RIGHT_LEAK = 1;
    return (
      rect.bottom > lastRect.bottom ||
      (rect.bottom === lastRect.bottom &&
        rect.right - lastRect.right > TOLERATED_RIGHT_LEAK)
    );
  };
  rectContainsOneOfRects = (rect: DOMRect, rects: DOMRect[]) => {
    for (let _i = 0, _len = rects.length; _i < _len; _i++) {
      const r = rects[_i];
      if (this.rectContainsNotEmptyRect(rect, r)) {
        return true;
      }
    }
    return false;
  };
  rectContainsNotEmptyRect = (
    possibleParent: DOMRect,
    potentialChild: DOMRect
  ) => {
    const MINIMAL_RECT_WIDTH = 2;
    const MINIMAL_RECT_HEIGHT = 1;
    const R = possibleParent;
    const r = potentialChild;
    return (
      !this.rectsAreEqual(R, r) &&
      R.top <= r.top &&
      R.right >= r.right &&
      R.bottom >= r.bottom &&
      R.left <= r.left &&
      r.height >= MINIMAL_RECT_HEIGHT &&
      r.width >= MINIMAL_RECT_WIDTH
    );
  };
  rectsAreEqual = (rectA: DOMRect, rectB: DOMRect) => {
    return (
      rectA === rectB ||
      (rectA.left === rectB.left &&
        rectA.right === rectB.right &&
        rectA.height === rectB.height &&
        rectA.width === rectB.width)
    );
  };
}

export type PointTarget<N extends ChildNode = ChildNode> = {
  node: N;
};
export class Point<N extends ChildNode = ChildNode> implements Coordinate {
  target: N;
  clientX: number;
  clientY: number;
  pageX: number;
  pageY: number;
  constructor(public pointer: Coordinate, public pointTarget: PointTarget<N>) {
    this.clientX = pointer.clientX;
    this.clientY = pointer.clientY;
    this.pageX = pointer.pageX;
    this.pageY = pointer.pageY;
    this.target = pointTarget.node;
  }

  static isTextPoint = (point: Point): point is Point<Text> =>
    NodeUtil.nodeIsText(point.target);
}

export class PointLocator {
  constructor(public environment: Environment) {}
  rangeContainsPoint = (range: RangyRange, point: Coordinate) => {
    const _ref = range.nativeRange.getClientRects();
    for (let _i = 0, _len = _ref.length; _i < _len; _i++) {
      const rect = _ref[_i];
      if (this.rectContainsPoint(rect, point)) {
        return true;
      }
    }
    return false;
  };
  nodeContainsPoint = (node: Node, point: Coordinate) => {
    const rects = NodeUtil.getRectsForNode(node);
    if (either.isLeft(rects)) {
      return false;
    }
    const _ref = rects.right;
    for (let _i = 0, _len = _ref.length; _i < _len; _i++) {
      let rect = _ref[_i];
      if (this.rectContainsPoint(rect, point)) {
        return true;
      }
    }
    return false;
  };

  rectContainsPoint = (rect: DOMRect | ClientRect, point: Coordinate) => {
    return (
      this.rectContainsPointVertically(rect, point) &&
      this.rectOrItsBoundsContainPointHorizontally(rect, point)
    );
  };

  rectContainsPointVertically = (
    rect: DOMRect | ClientRect,
    point: Coordinate
  ) => {
    const y = this.environment.isAppleDevice ? point.pageY : point.clientY;
    return y > rect.top && y < rect.bottom;
  };

  rectOrItsBoundsContainPointHorizontally = (
    rect: DOMRect | ClientRect,
    point: Coordinate
  ) => {
    const x = this.environment.isAppleDevice ? point.pageX : point.clientX;
    return x >= rect.left && x <= rect.right;
  };
}

export class SelectionDrawer {
  public canvas?: HTMLCanvasElement;
  public context?: CanvasRenderingContext2D;
  constructor(
    private rectangler: Rectangler,
    private environment: Environment,
    private contentContext: ContentContext,
    private settings: Options
  ) {
    this.canvas = this.createCanvas();
    this.context = this.canvas.getContext('2d') || undefined;
  }
  removeCanvas = () => {
    if (this.canvas) {
      this.canvas.remove();
    }
  };
  redraw = (range: RangyRange) => {
    this.updateCanvasBounds(range.nativeRange.getBoundingClientRect());
    return this.drawSelection(range);
  };
  clearSelection = () => {
    return this.updateCanvasBounds();
  };

  drawSelection = (range: RangyRange) => {
    if (!this.context) {
      return null;
    }

    const boundingClientRect = range.nativeRange.getBoundingClientRect();
    const rects = this.rectangler.getRectsFor(range);
    const SUBPIXEL_OFFSET = 0.5;
    const offsetX = SUBPIXEL_OFFSET - boundingClientRect.left;
    const offsetY = SUBPIXEL_OFFSET - boundingClientRect.top;
    this.context.save();
    this.context.translate(offsetX, offsetY);
    // if (isIOS()) {
    //  this.context.transform(0, 0, 0, 0, 0, 0);
    //}

    this.context.beginPath();
    for (let _i = 0, _len = rects.length; _i < _len; _i++) {
      const rect = rects[_i];
      this.context.rect(rect.left, rect.top, rect.width, rect.height);
    }
    this.context.closePath();
    this.context.fillStyle = this.settings.selectionColor;
    this.context.fill();
    this.context.restore();
    return rects.length
      ? {
          startHeight: rects[0].height,
          endHeight: rects[rects.length - 1].height,
        }
      : null;
  };

  //TODO: Probably shouldn't use this because we are using our own bounds element
  yOffset = () => {
    if (this.environment.isAppleDevice) {
      return 0;
    } else {
      //return this.contentContext.window.document.body.scrollTop;
      return 0;
    }
  };

  updateCanvasBounds = (_newBounds?: DOMRect | ClientRect) => {
    if (!this.canvas) {
      return;
    }
    const mapBounds = this.settings.mapBounds || defaultMapBounds;

    const containerBounds = !this.settings.boundsElement
      ? {
          x: 0,
          y: 0,
          top: 0,
          left: 0,
          width: 0,
          height: 0,
        }
      : mapBounds(this.settings.boundsElement);
    const newBounds = {
      x: _newBounds ? _newBounds.left : 0,
      y: _newBounds ? _newBounds.top : 0,
      top: _newBounds ? _newBounds.top : 0,
      left: _newBounds ? _newBounds.left : 0,
      width: _newBounds ? _newBounds.width : 0,
      height: _newBounds ? _newBounds.height : 0,
    };

    newBounds.top -= containerBounds.top;
    newBounds.left -= containerBounds.left;

    this.canvas.style.top = newBounds.top + this.yOffset() + 'px';
    this.canvas.style.left = newBounds.left + 'px';
    this.canvas.width = newBounds.width;
    this.canvas.height = newBounds.height;
  };

  createCanvas = () => {
    const canvas = this.contentContext.createElement('canvas');
    canvas.style.position = 'absolute';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '1';
    canvas.width = 0;
    canvas.height = 0;
    (
      this.settings.canvasContainer || this.contentContext.container
    ).appendChild(canvas);
    return canvas;
  };
}

type SelectionState = {
  range: RangyRange;
  startHeight: number;
  endHeight: number;
  selecting: boolean;
};
type Options = {
  /** Use this to control how pointer events are shifted/offset (for instance, pointerEvent.x = 500, but the actual content is at x = 1500 ). Particularly useful with epub.js's css column layout */
  pointerEventsBounds?: HTMLElement;
  selectionColor: CanvasFillStrokeStyles['fillStyle'];
  markerShiftY: number;
  onSelectionChange: (state?: SelectionState) => void;
  startMarker: HTMLElement;
  endMarker: HTMLElement;
  mapBounds?: (el: HTMLElement) => DOMRect;
  boundsElement: HTMLElement;
  /** Use this to control where the selection rect canvas is rendered */
  canvasContainer?: HTMLElement;
  /** The amount of time you need to hold your pointer on the screen for the text underneath to be selected */
  holdTime?: number;
};

export class CustomSelection {
  contextTranslator: ContextTranslator;
  pointerEventBinder: PointerEventBinder;
  selectionApplier: SelectionApplier;
  selectionStateStore: SelectionStateStore;
  constructor(public element: HTMLElement, public options: Options) {
    const environment = this.performEnvTests();
    const selectionStateStore = (this.selectionStateStore =
      new SelectionStateStore(options.onSelectionChange));
    const frameRequester = new FrameRequester();
    const contentContext = new ContentContext(this.element);
    const contextTranslator = (this.contextTranslator = new ContextTranslator(
      options.mapBounds,
      options.pointerEventsBounds
    ));
    const startMarker = new StartMarker(
      this.options.startMarker,
      contentContext,
      contextTranslator,
      this.options.mapBounds,
      this.options.boundsElement
    );
    const endMarker = new EndMarker(
      this.options.endMarker,
      contentContext,
      contextTranslator,
      this.options.mapBounds,
      this.options.boundsElement
    );
    const movingMarker = new MovingMarker(startMarker, endMarker);
    const markersWrapper = new MarkersWrapper(
      startMarker,
      endMarker,
      contentContext
    );

    const rectangler = new Rectangler(environment, contextTranslator);
    const selectionDrawer = new SelectionDrawer(
      rectangler,
      environment,
      contentContext,
      {
        ...this.options,
        markerShiftY: this.options.markerShiftY,
      }
    );

    const pointLocator = new PointLocator(environment);
    const pointTargetLocator = new PointTargetLocator(
      contentContext,
      markersWrapper,
      pointLocator
    );
    const pointFactory = new PointFactory(
      environment,
      contextTranslator,
      pointTargetLocator
    );
    const rightPointSnapper = new RightPointSnapper(
      pointFactory,
      contentContext
    );
    const belowPointSnapper = new BelowPointSnapper(
      pointFactory,
      contentContext
    );
    const pointToRangeConverter = new PointToRangeConverter(
      pointLocator,
      contentContext,
      rightPointSnapper,
      belowPointSnapper
    );

    const lastSelection = new LastSelection();
    const boundFactory = new SelectionBoundFactory(lastSelection, movingMarker);
    const wordRangeBuilder = new WordRangeBuilder(
      pointToRangeConverter,
      this.element
    );
    const selectionRangeBuilder = new SelectionRangeBuilder(
      pointToRangeConverter,
      boundFactory,
      movingMarker
    );
    const selectionConstructor = new SelectionConstructor(
      this.options,
      selectionRangeBuilder,
      markersWrapper,
      pointFactory,
      wordRangeBuilder,
      frameRequester,
      contentContext
    );
    const selectionApplier = (this.selectionApplier = new SelectionApplier(
      this.options,
      lastSelection,
      markersWrapper,
      selectionDrawer,
      selectionStateStore
    ));
    const pointerEventBus = new PointerEventBus(
      movingMarker,
      selectionApplier,
      selectionConstructor,
      selectionStateStore,
      markersWrapper,
      contentContext,
      contextTranslator,
      {
        holdTime: this.options.holdTime || 500,
      }
    );

    this.pointerEventBinder = new PointerEventBinder(
      this.element,
      markersWrapper,
      pointerEventBus
    );

    selectionApplier.clearSelection();
  }
  performEnvTests = function () {
    const env = new EnvironmentChecker();
    return {
      isWebkit: env.isWebkit(),
      isAppleDevice: env.isAppleDevice(),
      isAndroidLowerThanKitkat: env.isAndroidLowerThan('4.4'),
      isAndroidStackBrowser: env.isAndroidStackBrowser(),
    };
  };

  refresh = (transformation: {
    offsetX: number;
    offsetY: number;
    scale: number;
  }) => {
    if (transformation != null) {
      this.contextTranslator.setContentTransformationFromMarkersContext(
        transformation
      );
    }
    return this.selectionApplier.refreshSelection();
  };

  clear = () => {
    return this.selectionApplier.clearSelection();
  };
  remove = () => {
    this.selectionApplier.removeCanvas();
  };
  destroy = () => {
    this.selectionApplier.clearSelection();
    this.pointerEventBinder.destroyBindings();
    this.selectionStateStore.destroy();
    return;
  };
}
